// Type definitions for the v5 Audit Log surface.
//
// Mirrors the LLD §8 contract. The new BFF endpoints return rendered rows
// suitable for direct display; the detail endpoint returns the raw DDB
// item plus rendered fields for the expanded panel.
//
// Kept in this folder (not in @/types) because nothing outside the audit
// page consumes them today.

/** Actor block returned for each list event. */
export interface AuditActor {
  alias: string;
  displayName: string;
  /** "user" | "system" | "support" | "scim" | "deleted" */
  kind: string;
  /** Role used at the time of the action. May be empty. */
  role: string;
  email?: string;
  /** Two-letter initials for the avatar. Server-rendered when available. */
  initials?: string;
  /** Avatar background colour (hex). Server-rendered when available. */
  avatarColor?: string;
}

/** Target block — what was acted on. */
export interface AuditTarget {
  /** "role" | "rule" | "camera" | "video" | "user" | "location" | … */
  kind: string;
  id: string;
  displayName: string;
}

/** Diff hint rendered inline in the Details column. */
export interface AuditChange {
  field?: string;
  before?: string | number | boolean | null;
  after?: string | number | boolean | null;
  /** Free-form sentence ("invite sent", "9 users affected") rendered when no before/after. */
  summary?: string;
}

/** Single audit event as returned from the list endpoint. */
export interface AuditEvent {
  eventId: string;
  /** ISO-8601 timestamp. */
  timestamp: string;
  eventType: string;
  category: string;
  /** Human-readable label rendered from the catalog. */
  humanLabel: string;
  /** Optional sub-line: "cameras/ava1.ring.device…/motion_zones". */
  rawTarget?: string;
  actor: AuditActor;
  target?: AuditTarget;
  locationId?: string;
  locationName?: string;
  requestId?: string;
  sourceIp?: string;
  /** Rendered diff chips. Server may emit zero, one, or many. */
  changes?: AuditChange[];
  /** Free-form supplement when no structured changes apply. */
  detailsText?: string;
}

export interface AuditListResponse {
  events: AuditEvent[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  /** When true, the back-end has hit the OpenSearch 10k page-window cap and
   *  the UI must switch to Prev/Next-only navigation (no jumping to page N). */
  truncated?: boolean;
}

/** Per-axis facet entry. Counts reflect the *other* filters held constant. */
export interface FacetLocation {
  id: string;
  name: string;
  count: number;
}

export interface FacetEventType {
  type: string;
  category: string;
  human: string;
  count: number;
}

export interface FacetActor {
  alias: string;
  displayName: string;
  count: number;
}

export interface FacetCategory {
  category: string;
  count: number;
}

export interface AuditFacetsResponse {
  locations: FacetLocation[];
  event_types: FacetEventType[];
  actors: FacetActor[];
  categories: FacetCategory[];
}

/** Raw row from DDB returned by the detail endpoint. */
export interface AuditEventDetail extends AuditEvent {
  /** The full DDB item, opaque to the UI; rendered as JSON in the panel. */
  details?: Record<string, unknown>;
  /** Pre-stringified diff payload from the catalog. */
  changes_json?: Record<string, unknown> | string;
  /** Authentication provider (e.g. "midway") at the time of the event. */
  authProvider?: string;
  /** "session" | "scim" | "system" — write-time provenance. */
  source?: string;
}

/** All filter state that shows up as URL query params. */
export interface AuditFilterState {
  /** ISO date inclusive — yyyy-MM-dd. */
  from: string;
  /** ISO date inclusive — yyyy-MM-dd. */
  to: string;
  /** Multi-valued filters. */
  locationIds: string[];
  eventTypes: string[];
  actorAliases: string[];
  /** Free-text search; bound to ?q. */
  q: string;
  page: number;
  pageSize: 25 | 50 | 100;
  /** "today" | "yesterday" | "7d" | "30d" | "custom" — derived for the picker UI. */
  rangePreset: "today" | "yesterday" | "7d" | "30d" | "custom";
}

/** Names of the supported date presets. */
export const RANGE_PRESETS = ["today", "yesterday", "7d", "30d", "custom"] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number];

/** v5 mockup category-chip palette. Keys are uppercased categories. */
export const CATEGORY_TONES: Readonly<Record<string, string>> = Object.freeze({
  ROLE: "role",
  CAMERA: "camera",
  VIDEO: "video",
  MODE: "mode",
  LOCATION: "location",
  ACCESS: "access",
  INSTALLER: "installer",
  IDENTITY: "role",
  SESSION: "access",
  SYSTEM: "system",
});
